import { useEffect, useState, useCallback } from "react";
import { PageTitle } from "@/components/PageHeader";
import { api } from "@/lib/api";
import { useCachedState, cacheGet } from "@/lib/swr";
import { useDash } from "./ctx";
import { toast } from "sonner";
import { Lock,
  Phone, RefreshCw, CheckCircle2, XCircle, Clock, AlertTriangle, ShieldCheck,
  MessageSquare, Image, PhoneCall, Link2, Building2, ChevronDown, Trash2,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { NoAccess, PLATFORM_ROLES } from "@/components/NoAccess";
import { BuyNumberPanel } from "@/components/BuyNumberPanel";

const A2P_STYLES = {
  ready: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", icon: ShieldCheck },
  review: { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700", icon: Clock },
  failed: { bg: "bg-danger-50", border: "border-danger-200", text: "text-danger-700", icon: AlertTriangle },
  none: { bg: "bg-sand-100", border: "border-sand-200", text: "text-ink-700", icon: AlertTriangle },
};

function a2pTone(a2p) {
  if (!a2p) return "none";
  if (a2p.ready) return "ready";
  const s = (a2p.summary || "").toLowerCase();
  if (s.includes("action needed") || s.includes("failed")) return "failed";
  if (s.includes("review") || s.includes("progress") || s.includes("await")) return "review";
  return "none";
}

const STATUS_BADGE = {
  VERIFIED: "bg-emerald-100 text-emerald-700",
  APPROVED: "bg-emerald-100 text-emerald-700",
  IN_PROGRESS: "bg-amber-100 text-amber-700",
  IN_REVIEW: "bg-amber-100 text-amber-700",
  PENDING: "bg-amber-100 text-amber-700",
  FAILED: "bg-danger-100 text-danger-700",
};

export default function Numbers() {
  const { role, refreshTenants } = useDash();
  const [data, setData] = useCachedState("numbers-admin", null);
  const [a2p, setA2p] = useCachedState("numbers-a2p", null);
  const [loading, setLoading] = useState(() => cacheGet("numbers-admin") === undefined);
  const [busy, setBusy] = useState("");
  const [releasing, setReleasing] = useState(null);   // number pending release confirmation

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const base = process.env.REACT_APP_BACKEND_URL;
      const [numsRes, a2pRes] = await Promise.all([
        api.get(`/admin/twilio/numbers?base_url=${encodeURIComponent(base)}`),
        api.get("/admin/twilio/a2p"),
      ]);
      setData(numsRes.data);
      setA2p(a2pRes.data);
    } catch (e) {
      toast.error("Failed to load Twilio data");
    } finally {
      setLoading(false);
    }
  }, []);

  // Only fetch when the role is actually allowed — otherwise the page showed its "not available
  // to your role" gate AND a red "failed to load Twilio data" toast from the 403s behind it.
  useEffect(() => { if (PLATFORM_ROLES.includes(role)) load(); }, [load, role]);

  if (!PLATFORM_ROLES.includes(role)) return <NoAccess page="Numbers" />;

  const assign = async (num, tenant) => {
    setBusy(num.sid);
    try {
      const res = await api.post("/admin/twilio/assign-number", {
        tenant_id: tenant.id, phone_number: num.phone_number, number_sid: num.sid, wire_webhook: true,
        base_url: process.env.REACT_APP_BACKEND_URL,
      });
      if (res.data?.webhook && res.data.webhook.ok === false) {
        toast.warning(`Assigned to ${tenant.name}, but webhook wiring failed: ${res.data.webhook.error}`);
      } else {
        toast.success(`${num.phone_number} → ${tenant.name} (webhook wired)`);
      }
      await load();
      await refreshTenants?.();
    } catch (e) {
      toast.error("Assignment failed");
    } finally { setBusy(""); }
  };

  const unassign = async (num) => {
    if (!num.assigned_tenant_id) return;
    setBusy(num.sid);
    try {
      await api.post("/admin/twilio/unassign-number", { tenant_id: num.assigned_tenant_id });
      toast.success(`${num.phone_number} unassigned`);
      await load();
      await refreshTenants?.();
    } catch { toast.error("Failed"); }
    finally { setBusy(""); }
  };

  const fixWebhook = async (num) => {
    if (!num.assigned_tenant_id) { toast.error("Assign to a community first"); return; }
    setBusy(num.sid);
    try {
      const res = await api.post("/admin/twilio/assign-number", {
        tenant_id: num.assigned_tenant_id, phone_number: num.phone_number, number_sid: num.sid, wire_webhook: true,
        base_url: process.env.REACT_APP_BACKEND_URL,
      });
      if (res.data?.webhook?.ok === false) toast.error(`Webhook fix failed: ${res.data.webhook.error}`);
      else toast.success(`Webhook wired for ${num.phone_number}`);
      await load();
      await refreshTenants?.();
    } catch { toast.error("Failed"); }
    finally { setBusy(""); }
  };

  const release = async () => {
    const num = releasing;
    if (!num) return;
    setBusy(num.sid);
    try {
      const r = await api.post("/admin/twilio/numbers/release", { phone_number: num.phone_number, number_sid: num.sid });
      toast.success(`${num.phone_number} released back to Twilio${r.data?.dry_run ? " (simulated in preview)" : " — billing stops"}`);
      setReleasing(null);
      await load();
      await refreshTenants?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Twilio refused to release the number"); }
    finally { setBusy(""); }
  };

  const tone = a2pTone(a2p);
  const S = A2P_STYLES[tone];
  const numbers = data?.numbers || [];
  // What we HOLD vs what is actually in service. A held number keeps billing until it's released.
  const held = numbers.filter((n) => !n.assigned_tenant_id && !n.protected);
  const inService = numbers.filter((n) => n.assigned_tenant_id).length;

  return (
    <div className="space-y-6" data-testid="numbers-page">
      <div className="flex items-start justify-between">
        <div>
          <PageTitle title={<>Numbers &amp; Registration</>} about="Live from your Twilio account — assign numbers to communities and track SMS delivery readiness." />
        </div>
        <button data-testid="numbers-refresh-btn" onClick={load}
          className="rounded-full bg-sand-100 text-ink font-semibold px-5 py-2.5 flex items-center gap-2 hover:bg-sand-200 transition-colors">
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} strokeWidth={2} /> Refresh
        </button>
      </div>

      {role === "super_admin" && (
        <BuyNumberPanel tenants={data?.tenants || []} onBought={() => { load(); refreshTenants?.(); }} />
      )}

      {/* A2P 10DLC status banner */}
      <div data-testid="a2p-banner" className={`rounded-2xl border p-6 ${S.bg} ${S.border}`}>
        <div className="flex items-start gap-4">
          <div className={`h-11 w-11 rounded-xl bg-white flex items-center justify-center shrink-0 ${S.text}`}>
            <S.icon className="h-6 w-6" strokeWidth={1.75} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-heading text-lg font-bold text-ink">A2P 10DLC — SMS Delivery Status</span>
            </div>
            <p className={`mt-1 font-medium ${S.text}`} data-testid="a2p-summary">{a2p?.summary || "Loading…"}</p>

            {a2p?.configured && (
              <div className="mt-4 grid sm:grid-cols-2 gap-3">
                <div className="bg-white/70 rounded-xl p-3">
                  <div className="text-xs uppercase tracking-wide text-muted-foreground font-bold mb-1.5">Brand</div>
                  {(a2p.brands || []).length === 0 ? (
                    <span className="text-sm text-muted-foreground">Not registered</span>
                  ) : (a2p.brands || []).map((b) => (
                    <span key={b.sid} className={`inline-block text-xs font-bold px-2 py-1 rounded-md mr-2 ${STATUS_BADGE[b.status] || "bg-sand-200 text-ink-700"}`}>
                      {b.status || "UNKNOWN"}
                    </span>
                  ))}
                </div>
                <div className="bg-white/70 rounded-xl p-3">
                  <div className="text-xs uppercase tracking-wide text-muted-foreground font-bold mb-1.5">Campaign</div>
                  {(a2p.campaigns || []).length === 0 ? (
                    <span className="text-sm text-muted-foreground">Not registered</span>
                  ) : (a2p.campaigns || []).map((c, i) => (
                    <span key={i} className={`inline-block text-xs font-bold px-2 py-1 rounded-md mr-2 ${STATUS_BADGE[c.campaign_status] || "bg-sand-200 text-ink-700"}`}>
                      {c.campaign_status || "UNKNOWN"}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {a2p && !a2p.configured && (
              <p className="text-sm text-muted-foreground mt-2">Twilio credentials not configured on this environment.</p>
            )}
            <p className="text-xs text-muted-foreground mt-3">
              WhatsApp &amp; the in-app Simulator work regardless of this status. A2P 10DLC only affects standard SMS to 10-digit numbers.
            </p>
          </div>
        </div>
      </div>

      {/* Numbers table */}
      <div className="bg-white rounded-2xl border border-sand-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-sand-200 flex items-center justify-between gap-3 flex-wrap">
          <div className="font-heading font-bold text-ink flex items-center gap-2"><Phone className="h-4 w-4 text-redline-500" /> Twilio numbers</div>
          <div className="flex items-center gap-2 text-xs" data-testid="numbers-holding-summary">
            <span className="rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 font-semibold px-2.5 py-1">{inService} in service</span>
            <span className={`rounded-full border font-semibold px-2.5 py-1 ${held.length ? "bg-amber-50 border-amber-200 text-amber-800" : "bg-sand-100 border-sand-200 text-ink/60"}`}
              title="Held = on the account but assigned to nobody. It still bills every month until you release it.">
              {held.length} held{held.length ? " — still billing" : ""}
            </span>
            {numbers.some((n) => n.protected) && (
              <span className="rounded-full bg-sand-100 border border-sand-200 text-ink/60 font-semibold px-2.5 py-1" title="Serving another host (production) — this environment never touches them.">
                {numbers.filter((n) => n.protected).length} serving production
              </span>
            )}
            <span className="text-muted-foreground">{numbers.length} on account</span>
          </div>
        </div>

        {loading && !data ? (
          <div className="flex items-center justify-center h-40">
            <div className="h-7 w-7 rounded-full border-2 border-redline-500 border-t-transparent animate-spin" />
          </div>
        ) : !data?.configured ? (
          <div className="p-8 text-center text-muted-foreground">Twilio is not configured on this environment.</div>
        ) : numbers.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">No phone numbers on this Twilio account yet.</div>
        ) : (
          <div className="divide-y divide-sand-100">
            {numbers.map((num) => (
              <div key={num.sid} data-testid={`number-row-${num.sid}`} className={`px-4 sm:px-6 py-4 flex items-center gap-3 sm:gap-4 flex-wrap ${num.protected ? "bg-amber-50/40" : ""}`}>
                <div className="min-w-[160px]">
                  <div className="font-heading font-bold text-ink flex items-center gap-2">
                    <Phone className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} /> {num.phone_number}
                  </div>
                  <div className="flex items-center gap-1.5 mt-1">
                    {num.capabilities?.sms && <MessageSquare className="h-3.5 w-3.5 text-emerald-600" title="SMS capable (Twilio number capability)" />}
                    {num.capabilities?.mms && <Image className="h-3.5 w-3.5 text-emerald-600" title="MMS capable (Twilio number capability)" />}
                    {num.capabilities?.voice && <PhoneCall className="h-3.5 w-3.5 text-emerald-600" title="Voice capable (Twilio number capability)" />}
                  </div>
                </div>

                {/* Webhook status */}
                <div className="min-w-[150px] max-w-full">
                  {num.protected ? (
                    <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-amber-800" data-testid={`webhook-protected-${num.sid}`} title="This number's webhook points at production. Preview never rewires it.">
                      <Lock className="h-4 w-4" /> Live in production — protected
                    </span>
                  ) : num.webhook_ok ? (
                    <span className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-700" data-testid={`webhook-ok-${num.sid}`}>
                      <CheckCircle2 className="h-4 w-4" /> Webhook wired
                    </span>
                  ) : (
                    <button data-testid={`fix-webhook-${num.sid}`} onClick={() => fixWebhook(num)} disabled={busy === num.sid}
                      className="inline-flex items-center gap-1.5 text-sm font-medium text-redline-600 hover:text-redline-700 disabled:opacity-50">
                      <XCircle className="h-4 w-4" /> Not wired — fix
                    </button>
                  )}
                  {num.sms_url && (
                    <div className="text-[11px] text-muted-foreground truncate max-w-[220px] flex items-center gap-1 mt-0.5">
                      <Link2 className="h-3 w-3 shrink-0" /> {num.sms_url}
                    </div>
                  )}
                </div>

                {/* Assignment */}
                <div className="ml-auto flex items-center gap-2">
                  {num.protected ? (
                    <span className="text-xs text-amber-800/80 max-w-[260px]" data-testid={`protected-note-${num.sid}`}>Serving <b>{num.webhook_host}</b>. Assign or rewire it from production only.</span>
                  ) : num.assigned_tenant_name ? (
                    <>
                      <span data-testid={`assigned-to-${num.sid}`} className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1.5">
                        <Building2 className="h-3.5 w-3.5" /> {num.assigned_tenant_name}
                      </span>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button data-testid={`assign-menu-${num.sid}`} disabled={busy === num.sid}
                            className="inline-flex items-center gap-1 rounded-full border border-sand-200 text-ink/70 text-sm font-semibold px-3 py-1.5 hover:border-ink hover:text-ink transition-colors disabled:opacity-50">
                            {busy === num.sid ? "…" : <>Change <ChevronDown className="h-3.5 w-3.5" /></>}
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuLabel>Reassign to community</DropdownMenuLabel>
                          {(data.tenants || []).filter((t) => t.id !== num.assigned_tenant_id).map((t) => (
                            <DropdownMenuItem key={t.id} data-testid={`assign-${num.sid}-${t.id}`} onClick={() => assign(num, t)}>
                              {t.name}
                            </DropdownMenuItem>
                          ))}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem className="text-redline-600 font-medium" data-testid={`unassign-${num.sid}`} onClick={() => unassign(num)}>
                            Unassign this number
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </>
                  ) : (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button data-testid={`assign-menu-${num.sid}`} disabled={busy === num.sid}
                          className="inline-flex items-center gap-1 rounded-full bg-ink text-white text-sm font-semibold px-4 py-1.5 hover:bg-ink-700 transition-colors disabled:opacity-50">
                          {busy === num.sid ? "…" : <>Assign to community <ChevronDown className="h-3.5 w-3.5" /></>}
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuLabel>Assign to community</DropdownMenuLabel>
                        {(data.tenants || []).length === 0 && <DropdownMenuItem disabled>No communities yet</DropdownMenuItem>}
                        {(data.tenants || []).map((t) => (
                          <DropdownMenuItem key={t.id} data-testid={`assign-${num.sid}-${t.id}`} onClick={() => assign(num, t)}>
                            {t.name}
                          </DropdownMenuItem>
                        ))}
                        {role === "super_admin" && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem className="text-redline-600 font-medium" data-testid={`release-${num.sid}`} onClick={() => setReleasing(num)}>
                              Release back to Twilio
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <p className="text-xs text-muted-foreground">
        Assigning a number auto-points its inbound webhook to <span className="font-mono break-all">{data?.expected_webhook || "…/api/twilio/inbound"}</span>.
        {data?.preview ? <> <b>This is a preview environment:</b> numbers already serving another host (production) are locked here — nothing on this page can re-point them. Use the production console to assign or rewire live numbers.</> : <> Numbers wired to a preview URL show as “Not wired — fix”; hit fix to bring them to production.</>}
      </p>

      {/* Release a held number — typed-free but explicit, because this cannot be undone */}
      <Dialog open={!!releasing} onOpenChange={(v) => { if (!v) setReleasing(null); }}>
        <DialogContent className="max-w-md" data-testid="release-number-dialog">
          <DialogHeader><DialogTitle className="font-heading text-danger-600 flex items-center gap-2"><Trash2 className="h-5 w-5" /> Release {releasing?.phone_number}</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm text-ink-700">
            <p>This hands the number back to Twilio. The monthly charge stops and the number is <span className="font-semibold">gone for good</span> — Twilio can re-sell it, and nobody can text or call it again.</p>
            <p className="text-xs text-ink/60 bg-sand-50 border border-sand-200 rounded-lg p-2.5">
              Keep it instead if you want it ready for the next community — it stays on the account, assigned to nobody, and keeps billing (about $1.15/mo for a local number).
              {data?.preview && <> <b>Preview:</b> releases here are simulated — nothing is removed from Twilio.</>}
            </p>
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <button onClick={() => setReleasing(null)} className="rounded-full px-5 py-2.5 font-semibold text-ink-700 hover:bg-sand-100 transition-colors">Keep it</button>
            <button data-testid="release-number-confirm" onClick={release} disabled={!!busy}
              className="rounded-full bg-redline-500 text-white font-semibold px-6 py-2.5 hover:bg-redline-600 transition-colors disabled:opacity-40">
              {busy ? "Releasing…" : "Release it"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
